import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import eye from "../../assets/icons/eye.svg";
import eyeslash from "../../assets/icons/eye-slash.svg";
import * as Yup from "yup";
import { useLocation } from "react-router-dom";
import { PreferencesContext } from "../../PreferenceContext";
import { AlertType } from "../../shared/AlertType";
import usePasswordValidation from "../../hooks/usePasswordValidation";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { useAuth0 } from "@auth0/auth0-react";
import { getInviteDetail } from "../../service/InviteService";
import { CreateUser } from "../../service/AuthService";

const formInitialValues = {
  inviteUid: 0,
  clientUid: 0,
  firstName: "",
  lastName: "",
  newPassword: "",
  confirmPassword: "",
  showPassword: false,
  showConfirmPassword: false,
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required."),
  lastName: Yup.string().required("Last name is required."),
  newPassword: Yup.string()
    .matches(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*]).{8,}$/,
      "Must include an uppercase letter, a lowercase letter, a number and a special character"
    )
    .required("New password is required."),
  confirmPassword: Yup.string()
    .required("Confirm password is required.")
    .oneOf([Yup.ref("newPassword")], "Passwords must match"),
});

export default function SetupAccount() {
  const [values, setValues] = useState(formInitialValues);
  const [errors, setErrors] = useState<any>({});
  const [email, setEmail] = useState("");
  const { userContext } = useContext(PreferencesContext);
  const { loginWithRedirect } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [isVisible, setVisible] = useState(true);
  const [isExpired, setExpired] = useState(false);
  const [openWelcomeModal, setWelcomeModal] = useState(false);
  const [showAlert, setAlertMessage] = useState({
    isSuccess: false,
    type: "",
    message: "",
  });
  const [isDataLoading, setDataLoading] = useState(false);
  const [password, setPassword] = useState("");

  const [validLength, hasNumber, upperCase, lowerCase, specialChar] =
    usePasswordValidation({ firstPassword: password });

  const handleChange = (field: string, value: any) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleClickShowPassword = () => {
    setValues((prev) => ({ ...prev, showPassword: !prev.showPassword }));
  };

  const handleClickShowConfirmPassword = () => {
    setValues((prev) => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword,
    }));
  };

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }

  let urlParams = useQuery();

  async function fetchInviteDetail(auth_token: any) {
    setDataLoading(true);
    try {
      let params = new URLSearchParams();
      params.append("auth_token", auth_token.toString());
      params.append("clientUid", userContext.clientId.toString());
      const inviteDetail: any = await getInviteDetail(params);
      if (inviteDetail?.data?.data) {
        const data = inviteDetail.data.data;
        if (data === false) {
          setExpired(true);
          setVisible(false);
        } else {
          setEmail(data.INVITED_EMAIL);
          setValues((prev) => ({
            ...prev,
            clientUid: data.CLIENT_UID,
            inviteUid: data.UID,
          }));
          setVisible(true);
          setExpired(false);
        }
      } else {
        setVisible(false);
        setExpired(false);
      }
    } catch (error) {
      setAlertMessage({
        isSuccess: false,
        type: AlertType.Error,
        message: String(error),
      });
    } finally {
      setDataLoading(false);
    }
  }

  async function submitForm() {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      setErrors({});
    } catch (validationError: any) {
      const newErrors: any = {};
      validationError.inner.forEach((err: any) => {
        newErrors[err.path] = err.message;
      });
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const user: any = await CreateUser({
        clientId: values.clientUid,
        inviteUid: values.inviteUid,
        firstName: values.firstName,
        lastName: values.lastName,
        email: email,
        password: values.newPassword,
      });

      if (user) {
        setWelcomeModal(true);
      } else {
        setAlertMessage({
          isSuccess: false,
          type: AlertType.Error,
          message: "Something went wrong!",
        });
      }
    } catch (error) {
      setAlertMessage({
        isSuccess: false,
        type: AlertType.Error,
        message: String(error),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let auth_token = urlParams.get("t");
    if (auth_token) {
      fetchInviteDetail(auth_token);
    }
  }, []);

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isDataLoading}
      >
        <img
          loading="lazy"
          src="/loader.gif"
          style={{ height: "250px", width: "auto" }}
          alt="loading"
        />
      </Backdrop>

      <Grid className="set-up-account-main-grid">
        <Paper elevation={3} className="set-up-account-paper">
          <Grid className="set-up-account-wrapper">
            <Box className="set-up-account-box-container" sx={{ my: 2 }}>
              {isVisible ? (
                <Paper className="set-up-account-paper-container">
                  <Grid justifyContent="center">
                    <Typography className="set-up-account-title">
                      <b>Set up your account</b>
                    </Typography>
                    <Typography className="set-up-account-email">
                      <b>Email:</b> {email}
                    </Typography>
                  </Grid>

                  {showAlert.type && (
                    <Grid marginBottom={1}>
                      <Alert severity={showAlert.type === AlertType.Error ? "error" : "success"}>
                        {showAlert.message}
                      </Alert>
                    </Grid>
                  )}

                  <Grid container spacing={1}>
                    <Grid size={12}>
                      <Typography>First name</Typography>
                      <TextField
                        fullWidth
                        value={values.firstName}
                        onChange={(e) => handleChange("firstName", e.target.value)}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                      />
                    </Grid>

                    <Grid size={12}>
                      <Typography>Last name</Typography>
                      <TextField
                        fullWidth
                        value={values.lastName}
                        onChange={(e) => handleChange("lastName", e.target.value)}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                      />
                    </Grid>

                    <Grid size={12}>
                      <Typography>New password</Typography>
                      <TextField
                        fullWidth
                        type={values.showPassword ? "text" : "password"}
                        value={values.newPassword}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          handleChange("newPassword", e.target.value);
                        }}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleClickShowPassword}>
                                <img
                                  src={values.showPassword ? eyeslash : eye}
                                  alt="toggle"
                                  className="set-up-account-eye-icon"
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box className="validation-list">
                        <ul>
                          <li className={validLength ? "success" : "normal"}>
                            At least 8 characters
                          </li>
                          <li className={lowerCase ? "success" : "normal"}>
                            Lower case letters (a-z)
                          </li>
                          <li className={upperCase ? "success" : "normal"}>
                            Upper case letters (A-Z)
                          </li>
                          <li className={hasNumber ? "success" : "normal"}>
                            Numbers (0-9)
                          </li>
                          <li className={specialChar ? "success" : "normal"}>
                            Special characters (e.g. !@#$%^&*)
                          </li>
                        </ul>
                      </Box>
                    </Grid>

                    <Grid size={12}>
                      <Typography>Confirm password</Typography>
                      <TextField
                        fullWidth
                        type={values.showConfirmPassword ? "text" : "password"}
                        value={values.confirmPassword}
                        onChange={(e) => handleChange("confirmPassword", e.target.value)}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleClickShowConfirmPassword}>
                                <img
                                  src={values.showConfirmPassword ? eyeslash : eye}
                                  alt="toggle"
                                  className="set-up-account-eye-icon"
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid size={12} sx={{ textAlign: "end" }}>
                      <LoadingButton
                        color="primary"
                        sx={{ marginTop: 3 }}
                        loading={loading}
                        variant="contained"
                        onClick={submitForm}
                      >
                        Submit
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Paper>
              ) : (
                <Grid className="set-up-password-not-found">
                  <Typography className="set-up-password-not-found-text">
                    {isExpired ? "Invitation expired" : "Something went wrong"}
                  </Typography>
                </Grid>
              )}
            </Box>
          </Grid>
        </Paper>
      </Grid>

      <Dialog
        maxWidth="xs"
        fullWidth
        open={openWelcomeModal}
        className="set-up-account-welcome-dialog-box"
      >
        <Box className="set-up-account-welcome-img-box">
          <TaskAltIcon className="set-up-account-welecome-icon" />
        </Box>
        <DialogContent>
          <DialogContentText className="set-up-account-welcome-popup-text">
            <Typography className="set-up-account-welcome-popup-text1">
              Sign up success!
            </Typography>
            <Typography className="set-up-account-welcome-popup-text2">
              Please login on next page.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => loginWithRedirect()}>
            Sign In
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
